import { z } from "zod";

import { BannerSection } from "@widgets/banner";

import {
  ColorIconSchema,
  DetailsPopupInfoSchema,
  WorkTimeList,
} from "@shared/content-types/schemas/common.schema";
import { DynamicZoneSchema } from "@shared/content-types/schemas/dynamicZone.schema";
import { StrapiMediaSchema } from "@shared/content-types/schemas/media.schema";
import { TitleWithIconSchema } from "@shared/content-types/schemas/pageHero.schema";
import {
  FilterSchema,
  MetaSchema,
} from "@shared/content-types/schemas/pagination.schema";
import { SeoSchema } from "@shared/content-types/schemas/seo.schema";
import { UniversalGallerySectionSchema } from "@shared/ui/universal-gallery";

// Base schema for cultural object without other_objects to avoid circular reference
const CulturalObjectBaseSchema = z.object({
  id: z.number(),
  documentId: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  title: z.string(),
  uid: z.string().min(1),
  backgroundImage: StrapiMediaSchema.optional().nullable(),
  location: z.string(),
  travelTime: z.string(),
  travelTime2: z.string(),
  distanceFromResort: z.string(),
  coordinates: z.string(),
  workTimeList: z.array(WorkTimeList.dynamic).optional(),
  description: z.string().optional().nullable(),
  mainColor: z.string().optional(),
  onResort: z.boolean().optional().nullable(),
  content: DynamicZoneSchema.optional(),
  cultural_object_type: FilterSchema.optional(),
  gallerySection: UniversalGallerySectionSchema.base.nullable(),
  banner: BannerSection.base.nullable(),
  seo: SeoSchema.optional().nullable(),
  detailInfo: z.array(DetailsPopupInfoSchema).nullable(),
});

// Extended schema with other_objects
export const CulturalObjectSchema: z.ZodType<
  z.infer<typeof CulturalObjectBaseSchema> & {
    other_objects?: Array<z.infer<typeof CulturalObjectBaseSchema>> | null;
  }
> = CulturalObjectBaseSchema.extend({
  other_objects: z.lazy(() =>
    z.array(CulturalObjectBaseSchema).optional().nullable(),
  ),
});

export const PageDtoSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().optional(),
});

export const CulturalObjectsPageDtoSchema = z.object({
  id: z.number().int().positive(),
  documentId: z.string().min(1),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  publishedAt: z.string().datetime(),
  pageIcon: ColorIconSchema,
  titleWithIcon: TitleWithIconSchema,
  seo: SeoSchema.optional().nullable(),
});

export const CulturalObjectsDtoSchema = z.object({
  data: z.array(CulturalObjectSchema).min(0),
  meta: MetaSchema,
});

export const CulturalObjectsTypesDtoSchema = z.object({
  data: z.array(FilterSchema).min(0),
  meta: MetaSchema,
});
